import React, { useContext, useEffect, useRef, useState } from "react";
import OwTitle from "../OwTitle/OwTitle";
import OwButton from "../OwButton/OwButton";
import OwLabel from "../OwLabel/OwLabel";
import OwSortHeader from "../OwSortHeader/OwSortHeader";
import UIService from "../../../services/UIService";
import * as _ from "lodash-es";
import OwTranslate from "../OwTranslate/OwTranslate";
import SupplierContext from "../../../contexts/SupplierContext";
import UserContext from "../../../contexts/UserContext";
import IOnGetOrdersPickListDTO from "../../../models/DTO/IOnGetOrdersPickListDTO";
import SortOrderEnum from "../../../models/SortOrderEnum";
import moment from "moment";
import IGetOrdersPickListDTO from "../../../models/DTO/IGetOrdersPickListDTO";
import EndpointsEnum from "../../../models/EndpointsEnum";
import queryString from "query-string";
import { useLocation, useNavigate } from "react-router-dom";
import "./SimplePickList.scss";
import PickListComponentEnum from "../../../models/PickListComponentEnum";
import { PreformPickList } from "../../custom/PreformPickList/PreformPickList";
import Breadcrumbs from "../Breadcrumbs/Breadcrumbs";
import OwPrintButton from "../OwPrintButton/OwPrintButton";
import IBreadcrumbs from "../../../models/IBreadcrumbs";
import UiContext from "../../../contexts/UiContext";
import { SocketContext } from "../../../contexts/SocketContext";
import { Parser } from "@json2csv/plainjs";
import { IOption } from "../../../models/IOption";
import OwCheckbox from "../OwCheckbox/OwCheckbox";
import OwSelectOption from "../OwSelectOption/OwSelectOption";
import { useDailyRouteGroups } from "../../../hooks/useDailyRouteGroups";
import { DailyRouteCoverSheet } from "../DailyRouteCoverSheet/DailyRouteCoverSheet";
import { OwViewOptionsPanel } from "../OwViewOptionsPanel/OwViewOptionsPanel";
import { usePickListViewOptions } from "../../../hooks/pick_list/usePickListViewOptions";
import { usePickListViewOptionsUI } from "../../../hooks/pick_list/usePickListViewOptionsUI";
import SupplierService from "../../../services/SupplierService";
import IUpdateSupplierSettingsDTO from "../../../models/DTO/IUpdateSupplierSettingsDTO";
import IOnUpdateSupplierSettingsDTO from "../../../models/DTO/IOnUpdateSupplierSettingsDTO";
import INotification from "../../../models/INotification";
import NotificationPositionEnum from "../../../models/NotificationPositionEnum";
import NotificationLevelEnum from "../../../models/NotificationLevelEnum";
import IOrder_Item_PickListDTO from "../../../models/DTO/IOrder_Item_PickListDTO";
import PickListMatrixService, { IPickListMatrix } from "../../../services/PickListMatrixService";
import IOrdersDetailDTO from "../../../models/DTO/IOrdersDetailDTO";
import UserService from "../../../services/UserService";
import { useIntl } from "react-intl";
import { getPickItemQty } from "../../../services/PickListUomService";
import {
    formatPickItemOptions,
    getPickItemKey,
    getPickItemOptions,
    getSharedDeliveryDate,
} from "../../../services/PickListItemOptionsService";

const UNCATEGORIZED_GROUP = "Uncategorized";

// @page cannot be scoped to a selector, so the landscape rule is only mounted while the matrix layout is on.
const MATRIX_PAGE_STYLE = "@page { size: landscape; margin: 0.4in; }";
// The list layout's "Print Landscape" option, for the 8.5x11 template only (the 4x6 label has one orientation).
const LANDSCAPE_PAGE_STYLE = "@page { size: landscape; }";

type PickListLayout = "LIST" | "MATRIX";

/** A CSS string literal, safe inside a <style> element. */
const cssString = (value: string): string =>
    '"' +
    value
        .replace(/\\/g, "\\\\")
        .replace(/"/g, '\\"')
        .replace(/[\r\n]+/g, " ")
        .replace(/</g, "\\3c ") +
    '"';

const FOOTER_FONT = `font-family: system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", "Noto Sans", "Liberation Sans", Arial, sans-serif; font-size: 8pt; color: #000;`;

// Printed footer (page numbers, printed stamp) as @page margin boxes, so it repeats on every printed page.
// Chrome/Edge 131+ print them; other browsers print without a footer. Mirrors pickListFooterCss in
// @orderwerks/documents, which the emailed PDF uses.
export const buildFooterPageStyle = (
    printedText: string | null,
    pageText: { page: string; of: string } | null,
    isMinimal: boolean
): string => {
    const boxes: string[] = [];

    if (printedText) {
        boxes.push(`@bottom-left { content: ${cssString(printedText)}; ${FOOTER_FONT} }`);
    }

    if (pageText) {
        boxes.push(
            `@bottom-right { content: ${cssString(pageText.page + " ")} counter(page) ${cssString(" " + pageText.of + " ")} counter(pages); ${FOOTER_FONT} }`
        );
    }

    if (boxes.length === 0) {
        return "";
    }

    return `@page {${isMinimal ? " margin-bottom: 0.3in;" : ""} ${boxes.join(" ")} }`;
};

/**
 * Spreadsheets evaluate a cell that starts with =, +, -, @ (or a tab or carriage return) as a formula, and CSV
 * quoting does not stop that. A leading apostrophe makes it plain text. Applied to the free-text option values
 * customers typed; see https://owasp.org/www-community/attacks/CSV_Injection.
 */
export const escapeCsvFormula = (value: string): string => (/^[=+\-@\t\r]/.test(value) ? `'${value}` : value);

/** A random token for one mount's request ids; a UUID where the browser has one, else a random base-36 string. */
const newRequestPrefix = (): string =>
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : Math.random().toString(36).slice(2, 12);

type Props = {};

export const SimplePickList: React.FC<Props> = () => {
    const userModel = useContext(UserContext);
    const intl = useIntl();
    const supplierModel = useContext(SupplierContext);
    const location = useLocation();
    const socket = useContext(SocketContext)!;
    const uiContext = useContext(UiContext);
    const navigate = useNavigate();

    const isFromDailyRoutes = location.pathname.includes("routes/daily");
    const { dailyRouteGroups, loading: dailyRoutesLoading, hasDailyRoutes } = useDailyRouteGroups();

    const getNavItems = (): IBreadcrumbs[] => {
        const items: IBreadcrumbs[] = [];

        if (isFromDailyRoutes) {
            items.push({ title: "daily_routes", url: "/routes/daily" });
        } else {
            items.push({ title: "dashboard", url: "/dashboard" });
            items.push({ title: "orders", url: "/orders" });
        }

        items.push({ title: "pick_list", url: "" });

        return items;
    };

    const [pickListData, setPickListData] = useState<IOnGetOrdersPickListDTO>();
    const [groupedPickListData, setGroupedPickListData] = useState<Map<string, IOnGetOrdersPickListDTO>>(new Map());
    const [sortOrder, setSortOrder] = useState<SortOrderEnum>(SortOrderEnum.DESC);
    const [sortCol, setSortCol] = useState<string>("updated_at");
    const [navItems, setNavItems] = useState(getNavItems());
    const [template, setTemplate] = useState<"STANDARD" | "MINIMAL">("STANDARD");
    const [printOrders, setPrintOrders] = useState(true);
    // Bumped to re-run the daily-route fetch when the matrix layout is switched on after load.
    const [matrixRefetchNonce, setMatrixRefetchNonce] = useState(0);

    // Every pick list request carries a requestId the API echoes back. Only the latest request's response is
    // applied: when the user flips a grouping option while a request is pending, the earlier response can
    // arrive last and would otherwise replace the correctly grouped list (or, with daily routes, be filed under
    // the wrong route group). A response without a requestId comes from an API that does not echo it yet and
    // is accepted as before. The ids carry a per-mount random prefix: a plain counter would restart at 1 when
    // the user leaves and reopens the pick list, letting the previous mount's late response match the new one's.
    const requestPrefixRef = useRef(`pick-list-${newRequestPrefix()}`);
    const requestSeqRef = useRef(0);
    const expectedRequestIdRef = useRef<string | null>(null);

    const nextPickListRequestId = () => {
        requestSeqRef.current += 1;
        expectedRequestIdRef.current = `${requestPrefixRef.current}-${requestSeqRef.current}`;

        return expectedRequestIdRef.current;
    };

    const isCurrentPickListResponse = (data: IOnGetOrdersPickListDTO) =>
        data.requestId === undefined || data.requestId === expectedRequestIdRef.current;

    const supplierSettings = SupplierService.getSupplierSettings(supplierModel);
    const [viewOptions, handleViewOptionChange] = usePickListViewOptions(supplierSettings);
    const optionsPanelViewOptions = usePickListViewOptionsUI(viewOptions);

    const hasOrderBonus = !!supplierModel?.has_order_bonus;

    // Custom pick list components render their own layout; only the default renderer supports the matrix.
    const matrixLayoutSupported = (() => {
        switch (supplierModel.pick_list_component) {
            case PickListComponentEnum.PREFORM:
                return false;

            default:
                return true;
        }
    })();

    const pickListLayout: PickListLayout = matrixLayoutSupported && viewOptions.customerMatrixLayout ? "MATRIX" : "LIST";
    // The UoM column needs the API to group the lines by unit as well (includeUom), and the Item Options and
    // Delivery Date columns need it to keep a SKU with different options, or for different days, on separate
    // lines (includeItemOptions, includeDeliveryDate). The matrix keys its rows by catalog item alone, so those
    // columns apply to the list layout only; there the delivery date goes in the order column headers instead.
    // The custom Preform pick list sends dontGroupItems and renders its own layout, so none of these columns
    // apply there; leaving them off keeps a settings change from refetching a list that cannot show them.
    const lineColumnsSupported = pickListLayout === "LIST" && supplierModel.pick_list_component !== PickListComponentEnum.PREFORM;
    const showUom = viewOptions.showUom && lineColumnsSupported;
    const showItemOptions = viewOptions.showItemOptions && lineColumnsSupported;
    const showLineDeliveryDate = viewOptions.showDeliveryDate && lineColumnsSupported;
    // What the API is asked to group the lines by; a change means the pick list has to be fetched again.
    const lineGroupingKey = `${showUom}|${showItemOptions}|${showLineDeliveryDate}`;
    const layoutClass = pickListLayout === "MATRIX" ? " CustomerMatrix" : "";
    const matrixPageStyle = pickListLayout === "MATRIX" ? <style>{MATRIX_PAGE_STYLE}</style> : null;
    const landscapePageStyle =
        pickListLayout === "LIST" && template === "STANDARD" && viewOptions.landscape ? <style>{LANDSCAPE_PAGE_STYLE}</style> : null;

    const footerCss = buildFooterPageStyle(
        viewOptions.showPrintedDate
            ? `${intl.formatMessage({ id: "printed", defaultMessage: "Printed" })}: ${moment().format("MM/DD/YYYY h:mm A")}`
            : null,
        viewOptions.showPageNumbers
            ? {
                  page: intl.formatMessage({ id: "page", defaultMessage: "Page" }),
                  of: intl.formatMessage({ id: "page_of", defaultMessage: "of" }),
              }
            : null,
        template === "MINIMAL"
    );
    const footerPageStyle = footerCss ? <style dangerouslySetInnerHTML={{ __html: footerCss }} /> : null;

    const packingListTemplates: IOption[] = [
        { value: "STANDARD", label: "8.5x11" },
        { value: "MINIMAL", label: "4x6" },
    ];

    useEffect(() => {
        socket.on(EndpointsEnum.SupplierOnUpdateSupplierSettings, onUpdateSupplierSettings);

        if (!hasDailyRoutes) {
            socket.on(EndpointsEnum.OrderOnGetOrdersPickList, onGetOrdersPickList);
            getPickList();
        }

        return () => {
            socket.off(EndpointsEnum.SupplierOnUpdateSupplierSettings, onUpdateSupplierSettings);
            socket.off(EndpointsEnum.OrderOnGetOrdersPickList, onGetOrdersPickList);
        };
    }, []);

    // Sequential socket calls per daily route group
    useEffect(() => {
        if (!hasDailyRoutes || dailyRoutesLoading || dailyRouteGroups.length === 0) return;

        let currentGroupIndex = 0;
        const results = new Map<string, IOnGetOrdersPickListDTO>();

        const handleGroupResponse = (data: IOnGetOrdersPickListDTO) => {
            // A response to a request this run did not send (an earlier run's, or one sent with other
            // grouping options) must not be filed under the current group.
            if (!isCurrentPickListResponse(data)) {
                return;
            }

            const group = dailyRouteGroups[currentGroupIndex];
            results.set(group.dailyRoute.guid, data);
            currentGroupIndex++;

            if (currentGroupIndex < dailyRouteGroups.length) {
                emitPickListForGroup(dailyRouteGroups[currentGroupIndex]);
            } else {
                socket.off(EndpointsEnum.OrderOnGetOrdersPickList, handleGroupResponse);
                setGroupedPickListData(new Map(results));
            }
        };

        const emitPickListForGroup = (group: { orderGuids: string[] }) => {
            const pickListOrders = group.orderGuids.map((guid) => ({ guid }));
            const payload: IGetOrdersPickListDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel!.guid,
                requestId: nextPickListRequestId(),
                orders: pickListOrders,
            };

            switch (supplierModel.pick_list_component) {
                case PickListComponentEnum.PREFORM:
                    payload.dontGroupItems = true;
                    break;

                default:
                    payload.includeRootCategories = true;
                    payload.includeUom = showUom;
                    payload.includeItemOptions = showItemOptions;
                    payload.includeDeliveryDate = showLineDeliveryDate;

                    if (viewOptions.customerMatrixLayout) {
                        payload.includeOrderItemMatrix = true;
                    }
                    break;
            }

            socket.emit(EndpointsEnum.OrderGetOrdersPickList, payload);
        };

        socket.on(EndpointsEnum.OrderOnGetOrdersPickList, handleGroupResponse);
        emitPickListForGroup(dailyRouteGroups[0]);

        return () => {
            socket.off(EndpointsEnum.OrderOnGetOrdersPickList, handleGroupResponse);
        };
    }, [hasDailyRoutes, dailyRoutesLoading, dailyRouteGroups, matrixRefetchNonce]);

    // At most one refetch per switch into the matrix layout, so a response that never carries the map
    // (older API, error path) cannot trigger an endless fetch loop. Resets when the layout leaves matrix mode.
    const matrixRefetchAttemptedRef = useRef(false);

    // The per-order quantity map is opt-in, so switching the matrix on after the data loaded needs a refetch.
    useEffect(() => {
        if (pickListLayout !== "MATRIX") {
            matrixRefetchAttemptedRef.current = false;
            return;
        }

        if (matrixRefetchAttemptedRef.current) {
            return;
        }

        if (hasDailyRoutes) {
            const groups = Array.from(groupedPickListData.values());

            if (groups.length > 0 && groups.some((group) => !group.orderItemQtyMap)) {
                matrixRefetchAttemptedRef.current = true;
                setMatrixRefetchNonce((nonce) => nonce + 1);
            }
        } else if (pickListData && !pickListData.orderItemQtyMap) {
            matrixRefetchAttemptedRef.current = true;
            getPickList();
        }
    }, [pickListLayout, pickListData, groupedPickListData]);

    // Switching the UoM, Item Options or Delivery Date column changes how the API groups the lines (by catalog
    // item and unit, options or delivery date, or by catalog item alone), so the pick list is fetched again
    // whenever any of them flips after the first load.
    const fetchedWithGroupingRef = useRef(lineGroupingKey);

    useEffect(() => {
        if (fetchedWithGroupingRef.current === lineGroupingKey) {
            return;
        }

        fetchedWithGroupingRef.current = lineGroupingKey;

        if (hasDailyRoutes) {
            setMatrixRefetchNonce((nonce) => nonce + 1);
        } else {
            getPickList();
        }
    }, [lineGroupingKey]);

    const getOrderGuids = () => {
        const queryStringValues = queryString.parse(location.search);

        const qsOrders = queryStringValues.orders;

        if (_.isString(qsOrders)) {
            return qsOrders?.split(",");
        } else {
            return [];
        }
    };

    const getPickList = () => {
        const orderGuidStrings = getOrderGuids();

        const pickListOrders: { guid: string }[] = [];

        for (const str of orderGuidStrings) {
            pickListOrders.push({ guid: str });
        }

        const payload: IGetOrdersPickListDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel!.guid,
            requestId: nextPickListRequestId(),
            orders: pickListOrders,
        };

        switch (supplierModel.pick_list_component) {
            case PickListComponentEnum.PREFORM:
                payload.dontGroupItems = true;
                break;

            default:
                payload.includeRootCategories = true;
                payload.includeUom = showUom;
                payload.includeItemOptions = showItemOptions;
                payload.includeDeliveryDate = showLineDeliveryDate;

                if (viewOptions.customerMatrixLayout) {
                    payload.includeOrderItemMatrix = true;
                }
                break;
        }

        socket.emit(EndpointsEnum.OrderGetOrdersPickList, payload);
    };

    const onGetOrdersPickList = (data: IOnGetOrdersPickListDTO) => {
        // A superseded request's response (the grouping options changed since it was sent) is dropped.
        if (!isCurrentPickListResponse(data)) {
            return;
        }

        setPickListData(data);
    };

    const onUpdateSupplierSettings = (data: IOnUpdateSupplierSettingsDTO) => {
        const message: INotification = {
            position: NotificationPositionEnum.bc,
            level: NotificationLevelEnum.success,
            message: "successfully_updated_settings",
        };

        uiContext.notifyFn(null, message);
    };

    const saveViewOptions = () => {
        const payload: IUpdateSupplierSettingsDTO = {
            supplierGuid: supplierModel.guid,
            userGuid: userModel.guid,
            userToken: userModel.token,
            // @ts-expect-error partial settings payload
            settings: {
                simple_pick_list: {
                    show_sku: viewOptions.showSku,
                    show_item_name: viewOptions.showItemName,
                    show_description: viewOptions.showDescription,
                    show_control: viewOptions.showControl,
                    show_item_location: viewOptions.showItemLocation,
                    group_by_root_category: viewOptions.groupByRootCategory,
                    customer_matrix_layout: viewOptions.customerMatrixLayout,
                    show_customer_name: viewOptions.showCustomerName,
                    show_order_user: viewOptions.showOrderUser,
                    show_delivery_date: viewOptions.showDeliveryDate,
                    show_page_numbers: viewOptions.showPageNumbers,
                    show_printed_date: viewOptions.showPrintedDate,
                    show_uom: viewOptions.showUom,
                    repeat_order_header: viewOptions.repeatOrderHeader,
                    show_item_options: viewOptions.showItemOptions,
                    landscape: viewOptions.landscape,
                },
            },
        };

        socket.emit(EndpointsEnum.SupplierUpdateSupplierSettings, payload);
    };

    let tableTheme = "";

    if (!UIService.isMainViewLight()) {
        tableTheme = "table-dark text-white";
    }

    const getItemTotalQty = (item: IOrder_Item_PickListDTO) => {
        let totalQty = Number(item.total_qty);

        if (supplierModel && supplierModel.has_order_bonus) {
            totalQty += Number(item.total_bonus_qty_frozen);
        }

        return totalQty;
    };

    // With the UoM column on, a line counts in the unit it was ordered in (a case line counts cases) and the
    // totals add up the printed quantities. Off, everything stays in base units as it always has.
    const getItemQty = (item: IOrder_Item_PickListDTO) => (showUom ? getPickItemQty(supplierModel, item).qty : getItemTotalQty(item));

    const getItemUom = (item: IOrder_Item_PickListDTO) => getPickItemQty(supplierModel, item).uom;

    // delivery_date is a calendar date stored as midnight UTC, shown everywhere else with parseUTCDate.
    const formatDeliveryDate = (deliveryDate: string | Date | null | undefined) =>
        deliveryDate ? UserService.parseUTCDate(deliveryDate) : "";

    const groupItemsByRootCategory = (data: IOnGetOrdersPickListDTO): Map<string, IOrder_Item_PickListDTO[]> => {
        const groups = new Map<string, IOrder_Item_PickListDTO[]>();

        for (const item of data.orderItems) {
            const rootName = data.rootCategoryMap?.[item.catalog_item_id]?.name || UNCATEGORIZED_GROUP;

            if (!groups.has(rootName)) {
                groups.set(rootName, []);
            }

            groups.get(rootName)!.push(item);
        }

        const sortedGroupNames = Array.from(groups.keys()).sort((a, b) => {
            if (a === UNCATEGORIZED_GROUP) {
                return 1;
            }

            if (b === UNCATEGORIZED_GROUP) {
                return -1;
            }

            return a.localeCompare(b);
        });

        return new Map(sortedGroupNames.map((groupName) => [groupName, groups.get(groupName)!]));
    };

    // Matrix rows honour the root-category grouping; a single null-keyed group means "no grouping".
    const buildMatrixItemGroups = (data: IOnGetOrdersPickListDTO): Map<string | null, IOrder_Item_PickListDTO[]> => {
        const itemGroups = new Map<string | null, IOrder_Item_PickListDTO[]>();

        if (viewOptions.groupByRootCategory) {
            for (const [groupName, groupItems] of groupItemsByRootCategory(data)) {
                itemGroups.set(groupName, groupItems);
            }
        } else {
            itemGroups.set(null, data.orderItems);
        }

        return itemGroups;
    };

    const buildPickListMatrix = (data: IOnGetOrdersPickListDTO): IPickListMatrix => {
        return PickListMatrixService.buildPickListMatrix(data, buildMatrixItemGroups(data), hasOrderBonus, {
            includeDeliveryDate: viewOptions.showDeliveryDate,
        });
    };

    // Matrix rows are labelled by SKU and/or name; SKU is forced on when both are off.
    const showMatrixSkuCol = viewOptions.showSku || !viewOptions.showItemName;

    // Replaces pound symbols, which the export's old encodeURI left unencoded and so cut the data URI short.
    // The export now encodes the whole payload (encodeURIComponent), but the existing columns keep this so
    // their output does not change; the Item Options column, new in this change, keeps "#" as typed.
    const sanitizeCsvValue = (value: string) => {
        return value.indexOf("#") >= 0 ? value.replaceAll("#", "No.") : value;
    };

    const buildCsvHeaderRow = () => {
        const headerRow: string[] = [];

        if (viewOptions.groupByRootCategory) {
            headerRow.push("Root Category");
        }

        headerRow.push("Qty");

        if (showUom) {
            headerRow.push("UoM");
        }

        if (viewOptions.showSku) {
            headerRow.push("SKU");
        }

        if (viewOptions.showItemName) {
            headerRow.push("Name");
        }

        if (viewOptions.showDescription) {
            headerRow.push("Description");
        }

        if (showItemOptions) {
            headerRow.push("Item Options");
        }

        if (viewOptions.showItemLocation) {
            headerRow.push("Item Location");
        }

        if (showLineDeliveryDate) {
            headerRow.push("Delivery Date");
        }

        if (viewOptions.showControl) {
            headerRow.push("Qty. Picked");
        }

        return headerRow;
    };

    const buildCsvItemRow = (item: IOrder_Item_PickListDTO, rootCategoryName?: string) => {
        const row: { [key: string]: string } = {};

        if (viewOptions.groupByRootCategory) {
            row["Root Category"] = sanitizeCsvValue(rootCategoryName || UNCATEGORIZED_GROUP);
        }

        row["Qty"] = `${getItemQty(item)}`;

        if (showUom) {
            row["UoM"] = sanitizeCsvValue(getItemUom(item));
        }

        if (viewOptions.showSku) {
            row["SKU"] = sanitizeCsvValue(item.Catalog_Item?.sku || "");
        }

        if (viewOptions.showItemName) {
            row["Name"] = sanitizeCsvValue(item.Catalog_Item?.name || "");
        }

        if (viewOptions.showDescription) {
            row["Description"] = sanitizeCsvValue(item.Catalog_Item?.description || "");
        }

        if (showItemOptions) {
            // Option values are customer-typed free text, the one cell that can start with a formula character.
            row["Item Options"] = escapeCsvFormula(formatPickItemOptions(item));
        }

        if (viewOptions.showItemLocation) {
            row["Item Location"] = `${item.Catalog_Item?.row || ""} ${item.Catalog_Item?.bin || ""}`.trim();
        }

        if (showLineDeliveryDate) {
            row["Delivery Date"] = formatDeliveryDate(item.delivery_date);
        }

        if (viewOptions.showControl) {
            row["Qty. Picked"] = "";
        }

        return row;
    };

    const buildCsvRowsFromPickListData = (data: IOnGetOrdersPickListDTO) => {
        let pickItemsArr: { [key: string]: string }[] = [];

        if (viewOptions.groupByRootCategory) {
            for (const [groupName, groupItems] of groupItemsByRootCategory(data)) {
                pickItemsArr = [...pickItemsArr, ...groupItems.map((item) => buildCsvItemRow(item, groupName))];
            }
        } else {
            pickItemsArr = data.orderItems.map((item) => buildCsvItemRow(item));
        }

        const orders =
            data.orders.map((order) => {
                const vendor = sanitizeCsvValue(order.vendor || "");

                return { Qty: `${order.name} (${vendor})` };
            }) || [];

        return [...pickItemsArr, ...orders];
    };

    const buildListCsvContent = () => {
        const headerRow = buildCsvHeaderRow();

        let rows: any[] = [];

        if (hasDailyRoutes && groupedPickListData.size > 0) {
            for (const [, data] of groupedPickListData) {
                rows = [...rows, ...buildCsvRowsFromPickListData(data)];
            }
        } else if (pickListData) {
            rows = buildCsvRowsFromPickListData(pickListData);
        }

        const parser = new Parser({ fields: headerRow });

        return parser.parse(rows);
    };

    // One CSV table in the same shape as the on-screen matrix. Field values are keyed by order guid so two
    // orders with identical header text never collide; the label carries the human-readable header.
    const buildMatrixCsvContent = (data: IOnGetOrdersPickListDTO) => {
        const matrix = buildPickListMatrix(data);
        const fields: { label: string; value: string }[] = [];

        if (viewOptions.groupByRootCategory) {
            fields.push({ label: "Root Category", value: "root_category" });
        }

        if (showMatrixSkuCol) {
            fields.push({ label: "SKU", value: "sku" });
        }

        if (viewOptions.showItemName) {
            fields.push({ label: "Name", value: "name" });
        }

        for (const column of matrix.columns) {
            fields.push({
                label: sanitizeCsvValue(PickListMatrixService.formatColumnHeaderText(column)),
                value: `col_${column.orderGuid}`,
            });
        }

        fields.push({ label: "Total", value: "total" });

        const rows: { [key: string]: string | number }[] = [];

        for (const group of matrix.groups) {
            for (const row of group.rows) {
                const csvRow: { [key: string]: string | number } = {};

                if (viewOptions.groupByRootCategory) {
                    csvRow["root_category"] = sanitizeCsvValue(group.name || UNCATEGORIZED_GROUP);
                }

                if (showMatrixSkuCol) {
                    csvRow["sku"] = sanitizeCsvValue(row.item.Catalog_Item?.sku || "");
                }

                if (viewOptions.showItemName) {
                    csvRow["name"] = sanitizeCsvValue(row.item.Catalog_Item?.name || "");
                }

                matrix.columns.forEach((column, index) => {
                    csvRow[`col_${column.orderGuid}`] = row.cells[index] ?? "";
                });

                csvRow["total"] = row.rowTotal;

                rows.push(csvRow);
            }
        }

        const totalRow: { [key: string]: string | number } = {
            [showMatrixSkuCol ? "sku" : "name"]: "Total Pieces",
            total: matrix.grandTotal,
        };

        matrix.columns.forEach((column, index) => {
            totalRow[`col_${column.orderGuid}`] = matrix.columnTotals[index];
        });

        rows.push(totalRow);

        const parser = new Parser({ fields });

        return parser.parse(rows);
    };

    const buildMatrixCsvExport = () => {
        if (hasDailyRoutes && groupedPickListData.size > 0) {
            const blocks: string[] = [];

            for (const group of dailyRouteGroups) {
                const data = groupedPickListData.get(group.dailyRoute.guid);

                if (!data) {
                    continue;
                }

                blocks.push(`${sanitizeCsvValue(group.dailyRoute.name || "")}\n${buildMatrixCsvContent(data)}`);
            }

            return blocks.join("\n\n");
        }

        if (pickListData) {
            return buildMatrixCsvContent(pickListData);
        }

        return "";
    };

    const exportPickListCSV = () => {
        let csvContent = "";

        switch (pickListLayout) {
            case "MATRIX":
                csvContent = buildMatrixCsvExport();
                break;

            default:
                csvContent = buildListCsvContent();
                break;
        }

        const yearMonthDay = moment().format("YYYY-MM-DD");

        // encodeURIComponent, not encodeURI: the latter leaves "#" (and "?") unencoded, which ends the data URI early.
        const encodedUri = `data:text/csv;charset=utf-8,${encodeURIComponent(csvContent)}`;
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `${yearMonthDay}-Pick-List.csv`);

        link.click();
    };

    const sortPickList = (newSortCol: string, newSortOrder: SortOrderEnum) => {
        // Sortable headers render before the socket response; nothing to sort yet.
        if (!pickListData) {
            return;
        }

        let orderItems = _.sortBy(pickListData.orderItems, (item) => {
            switch (newSortCol) {
                case "item_location":
                    return `${item.Catalog_Item?.row || ""} ${item.Catalog_Item?.bin || ""}`.trim();

                default:
                    // @ts-expect-error dynamic catalog item property
                    return item.Catalog_Item[newSortCol];
            }
        });

        if (pickListData && orderItems) {
            if (newSortOrder === SortOrderEnum.DESC) {
                orderItems = orderItems.reverse();
            }

            pickListData.orderItems = orderItems;

            setSortOrder(newSortOrder);
            setSortCol(newSortCol);
            setPickListData(pickListData);
        }
    };

    // One order in the Orders checklist: name, then customer, who placed it and its delivery date when enabled.
    const renderOrderLine = (order: IOrdersDetailDTO) => {
        const customer = viewOptions.showCustomerName ? order?.vendor : null;
        const userName = viewOptions.showOrderUser ? order.user?.name : null;
        const deliveryDate = viewOptions.showDeliveryDate ? formatDeliveryDate(order.delivery_date) : "";

        return (
            // Three across on a letter page and two on a 4x6 label; at four across most customer names wrapped.
            <div key={order.guid} className={"pick-order col-6 col-sm-4"}>
                <div className={"pick-order-name"}>
                    <i className={"far fa-square"} /> {order?.name}
                </div>
                {customer || userName || deliveryDate ? (
                    <div className={"pick-order-details"}>
                        {customer ? <div className={"pick-order-customer"}>{customer}</div> : null}
                        {userName || deliveryDate ? (
                            <div className={"pick-order-meta"}>
                                {userName ? (
                                    <div>
                                        <OwTranslate defaultMessage={"User"} id={"user"} />: {userName}
                                    </div>
                                ) : null}
                                {deliveryDate ? (
                                    <div>
                                        <OwTranslate defaultMessage={"Delivery Date"} id={"delivery_date"} />: {deliveryDate}
                                    </div>
                                ) : null}
                            </div>
                        ) : null}
                    </div>
                ) : null}
            </div>
        );
    };

    const ordersDetail = _.map(pickListData?.orders, renderOrderLine);

    const orders = (
        <div className={printOrders ? "" : "d-print-none"}>
            <div className={"d-print-none mt-4"}>
                <OwCheckbox
                    fn={() => {
                        setPrintOrders(!printOrders);
                    }}
                    checked={printOrders}
                    label={"print_orders_detail"}
                />
            </div>
            <OwTitle className={"mt-3"} title={"orders"} tag={"h5"} />
            <div className={"row d-flex"}>{ordersDetail}</div>
        </div>
    );

    const visibleColCount =
        1 +
        (showUom ? 1 : 0) +
        (viewOptions.showSku ? 1 : 0) +
        (viewOptions.showItemName ? 1 : 0) +
        (viewOptions.showDescription ? 1 : 0) +
        (showItemOptions ? 1 : 0) +
        (viewOptions.showItemLocation ? 1 : 0) +
        (showLineDeliveryDate ? 1 : 0) +
        (viewOptions.showControl ? 1 : 0);

    const selectTemplate = (option: IOption) => {
        setTemplate(option.value as "STANDARD" | "MINIMAL");
    };

    const viewPackingLists = () => {
        if (hasDailyRoutes) {
            const params = new URLSearchParams(location.search);
            const drParam = params.get("dailyRoutes") || "";
            navigate(`/routes/daily/packing_list?dailyRoutes=${drParam}`);
        } else {
            const orderGuidStrings = getOrderGuids();
            const basePath = isFromDailyRoutes ? "/routes/daily" : "/orders";
            navigate(`${basePath}/packing_list?orders=${orderGuidStrings?.join(",")}`);
        }
    };

    /**
     * The row repeated at the top of every printed page (it lives in the table's thead): the customer and order
     * number of a single order's pick list; "N orders" when several orders are combined, since they have no
     * single customer.
     */
    const renderOrderHeaderRow = (data: IOnGetOrdersPickListDTO | undefined) => {
        if (!viewOptions.repeatOrderHeader || !data) {
            return null;
        }

        let content: React.ReactNode;

        // With the delivery date on, the date follows whenever every order on the list is for the same day.
        const deliveryDate = viewOptions.showDeliveryDate ? formatDeliveryDate(getSharedDeliveryDate(data.orders)) : "";
        const deliveryDateSpan = deliveryDate ? (
            <span className={"pick-order-header-delivery"}>
                <OwTranslate defaultMessage={"Delivery Date"} id={"delivery_date"} />: {deliveryDate}
            </span>
        ) : null;

        if (data.orders.length === 1) {
            const order = data.orders[0];

            content = (
                <>
                    {order.vendor ? <span className={"pick-order-header-customer"}>{order.vendor}</span> : null}
                    <span className={"pick-order-header-order"}>{order.name}</span>
                    {deliveryDateSpan}
                </>
            );
        } else {
            content = (
                <>
                    <span className={"pick-order-header-order"}>
                        {intl.formatMessage({ id: "n_orders", defaultMessage: "{count} orders" }, { count: data.orders.length })}
                    </span>
                    {deliveryDateSpan}
                </>
            );
        }

        return (
            <tr className={"order-header"}>
                <th colSpan={visibleColCount}>{content}</th>
            </tr>
        );
    };

    const renderItemTbody = (item: IOrder_Item_PickListDTO) => {
        let extendedDetails: any = null;

        if (item.note) {
            extendedDetails = (
                <tr>
                    <td></td>
                    <td colSpan={visibleColCount - 1}>
                        <b>
                            <OwTranslate defaultMessage={"note"} id={"note"} />:
                        </b>{" "}
                        {item.note}
                    </td>
                </tr>
            );
        }

        return (
            // Aggregated rows carry no guid; a catalog item can have one row per UoM set unit, delivery date and
            // set of item options.
            <tbody key={item.guid || getPickItemKey(item)}>
                <tr>
                    <td>{getItemQty(item)}</td>
                    {showUom ? <td className={"no-wrap"}>{getItemUom(item)}</td> : null}
                    {viewOptions.showSku ? <td>{item.Catalog_Item?.sku}</td> : null}
                    {viewOptions.showItemName ? <td>{item.Catalog_Item?.name}</td> : null}
                    {viewOptions.showDescription ? <td>{item.Catalog_Item?.description}</td> : null}
                    {showItemOptions ? (
                        <td className={"pick-item-options"}>
                            {getPickItemOptions(item).map((option, index) => (
                                <div key={index}>
                                    {/* An unnamed option shows its value alone, as the CSV export does. */}
                                    {option.name ? <b>{option.name}:</b> : null}
                                    {option.name ? " " : null}
                                    {option.value}
                                </div>
                            ))}
                        </td>
                    ) : null}
                    {viewOptions.showItemLocation ? (
                        <td>
                            {item.Catalog_Item?.row || item.Catalog_Item?.bin ? (
                                <div className={"d-flex"}>
                                    <div className={"me-2"}>{item.Catalog_Item?.row}</div>
                                    <div>{item.Catalog_Item?.bin}</div>
                                </div>
                            ) : (
                                <>&nbsp;</>
                            )}
                        </td>
                    ) : null}
                    {showLineDeliveryDate ? <td className={"no-wrap"}>{formatDeliveryDate(item.delivery_date)}</td> : null}
                    {viewOptions.showControl ? (
                        <td>
                            {template === "STANDARD" ? (
                                <div>
                                    <OwLabel title={"qty_picked"} />:
                                </div>
                            ) : null}
                        </td>
                    ) : null}
                </tr>
                {extendedDetails}
            </tbody>
        );
    };

    const renderOrdersDetail = (data: IOnGetOrdersPickListDTO) => {
        const groupOrdersDetail = _.map(data.orders, renderOrderLine);

        return (
            <div className={printOrders ? "" : "d-print-none"}>
                <OwTitle className={"mt-3"} title={"orders"} tag={"h5"} />
                <div className={"row d-flex"}>{groupOrdersDetail}</div>
            </div>
        );
    };

    /**
     * The list layout is mirrored in @orderwerks/documents (orderwerks-shared: packages/documents/src/pick-list),
     * which renders the emailed pick list PDF. Until this component moves onto that package, make any layout or
     * field change in both places, or the screen and the PDF will differ.
     */
    const renderPickListTable = (data: IOnGetOrdersPickListDTO | undefined, sortable: boolean) => {
        const orderItems = data?.orderItems || [];
        const pickTotalQty = _.sumBy(orderItems, getItemQty);

        let bodySections: React.ReactNode[] = [];

        if (data && viewOptions.groupByRootCategory) {
            for (const [groupName, groupItems] of groupItemsByRootCategory(data)) {
                bodySections.push(
                    <tbody key={`group-header-${groupName}`}>
                        <tr className={"group-header"}>
                            <td colSpan={visibleColCount}>
                                {groupName === UNCATEGORIZED_GROUP ? (
                                    <OwTranslate defaultMessage={"Uncategorized"} id={"uncategorized"} />
                                ) : (
                                    groupName
                                )}
                            </td>
                        </tr>
                    </tbody>
                );

                bodySections = [...bodySections, ...groupItems.map(renderItemTbody)];

                bodySections.push(
                    <tbody key={`group-total-${groupName}`}>
                        <tr>
                            <td colSpan={visibleColCount}>
                                {_.sumBy(groupItems, getItemQty)} <OwLabel title={"total"} noBottomMargin={true} />
                            </td>
                        </tr>
                    </tbody>
                );
            }
        } else {
            bodySections = orderItems.map(renderItemTbody);
        }

        return (
            <div className={"table-responsive"}>
                <table className={"orders-table table " + tableTheme}>
                    <thead>
                        {renderOrderHeaderRow(data)}
                        <tr>
                            <th>
                                <OwLabel title={"qty"} rowHasSort={true} />
                            </th>
                            {showUom ? (
                                <th>
                                    <OwLabel title={"uom"} rowHasSort={true} />
                                </th>
                            ) : null}
                            {viewOptions.showSku ? (
                                <th>
                                    {sortable ? (
                                        <OwSortHeader
                                            title={"sku"}
                                            currentSortCol={sortCol}
                                            currentSortOrder={sortOrder}
                                            sortFn={sortPickList}
                                        />
                                    ) : (
                                        <OwLabel title={"sku"} rowHasSort={true} />
                                    )}
                                </th>
                            ) : null}
                            {viewOptions.showItemName ? (
                                <th>
                                    <OwLabel title={"name"} rowHasSort={true} />
                                </th>
                            ) : null}
                            {viewOptions.showDescription ? (
                                <th>
                                    <OwLabel title={"description"} rowHasSort={true} />
                                </th>
                            ) : null}
                            {showItemOptions ? (
                                <th>
                                    <OwLabel title={"item_options"} rowHasSort={true} />
                                </th>
                            ) : null}
                            {viewOptions.showItemLocation ? (
                                <th>
                                    {sortable ? (
                                        <OwSortHeader
                                            title={"item_location"}
                                            currentSortCol={sortCol}
                                            currentSortOrder={sortOrder}
                                            sortFn={sortPickList}
                                        />
                                    ) : (
                                        <OwLabel title={"item_location"} rowHasSort={true} />
                                    )}
                                </th>
                            ) : null}
                            {showLineDeliveryDate ? (
                                <th>
                                    <OwLabel className={"no-wrap"} title={"delivery_date"} rowHasSort={true} />
                                </th>
                            ) : null}
                            {viewOptions.showControl ? (
                                <th>
                                    <OwLabel
                                        className={"no-wrap"}
                                        title={template === "STANDARD" ? "control" : "qty_picked"}
                                        rowHasSort={true}
                                    />
                                </th>
                            ) : null}
                        </tr>
                    </thead>
                    {bodySections}
                    <tbody>
                        <tr>
                            <td colSpan={visibleColCount}>
                                {pickTotalQty} <OwLabel title={"total"} noBottomMargin={true} />
                            </td>
                        </tr>
                    </tbody>
                    <tfoot />
                </table>
            </div>
        );
    };

    const renderMatrixTable = (data: IOnGetOrdersPickListDTO | undefined, sortable: boolean) => {
        const matrix = buildPickListMatrix(data || { orders: [], orderItems: [] });
        const labelColCount = PickListMatrixService.getMatrixLabelColCount(viewOptions.showSku, viewOptions.showItemName);
        const totalColCount = labelColCount + matrix.columns.length + 1;

        const renderGroupName = (groupName: string) => {
            return groupName === UNCATEGORIZED_GROUP ? <OwTranslate defaultMessage={"Uncategorized"} id={"uncategorized"} /> : groupName;
        };

        const renderTotalsRow = (label: React.ReactNode, columnTotals: number[], total: number, className: string) => {
            return (
                <tr className={className}>
                    <td colSpan={labelColCount} className={"label-col"}>
                        {label}
                    </td>
                    {matrix.columns.map((column, index) => (
                        <td key={column.orderGuid} className={"qty-cell"}>
                            {columnTotals[index]}
                        </td>
                    ))}
                    <td className={"total-col"}>{total}</td>
                </tr>
            );
        };

        const bodySections: React.ReactNode[] = [];

        for (const group of matrix.groups) {
            if (group.name !== null) {
                bodySections.push(
                    <tbody key={`group-header-${group.name}`}>
                        <tr className={"group-header"}>
                            <td colSpan={totalColCount}>{renderGroupName(group.name)}</td>
                        </tr>
                    </tbody>
                );
            }

            for (const row of group.rows) {
                bodySections.push(
                    <tbody key={row.item.catalog_item_id}>
                        <tr>
                            {showMatrixSkuCol ? <td className={"label-col"}>{row.item.Catalog_Item?.sku}</td> : null}
                            {viewOptions.showItemName ? <td className={"label-col"}>{row.item.Catalog_Item?.name}</td> : null}
                            {row.cells.map((cell, index) => (
                                <td key={matrix.columns[index].orderGuid} className={"qty-cell"}>
                                    {cell ?? ""}
                                </td>
                            ))}
                            <td className={"total-col"}>{row.rowTotal}</td>
                        </tr>
                    </tbody>
                );
            }

            if (group.name !== null) {
                bodySections.push(
                    <tbody key={`group-total-${group.name}`}>
                        {renderTotalsRow(
                            <>
                                {renderGroupName(group.name)} <OwLabel title={"total"} noBottomMargin={true} />
                            </>,
                            group.columnTotals,
                            group.groupTotal,
                            "group-total"
                        )}
                    </tbody>
                );
            }
        }

        return (
            <div className={"table-responsive"}>
                <table className={"matrix-table orders-table table " + tableTheme}>
                    <thead>
                        <tr>
                            {showMatrixSkuCol ? (
                                <th className={"label-col"}>
                                    {sortable ? (
                                        <OwSortHeader
                                            title={"sku"}
                                            currentSortCol={sortCol}
                                            currentSortOrder={sortOrder}
                                            sortFn={sortPickList}
                                        />
                                    ) : (
                                        <OwLabel title={"sku"} rowHasSort={true} />
                                    )}
                                </th>
                            ) : null}
                            {viewOptions.showItemName ? (
                                <th className={"label-col"}>
                                    <OwLabel title={"name"} rowHasSort={true} />
                                </th>
                            ) : null}
                            {matrix.columns.map((column) => (
                                <th key={column.orderGuid} className={"order-col"}>
                                    <div className={"vendor"}>{PickListMatrixService.getColumnTitle(column)}</div>
                                    {column.contactName ? <div className={"contact"}>{column.contactName}</div> : null}
                                    {column.phone ? <div className={"phone"}>{column.phone}</div> : null}
                                    {column.deliveryDate ? <div className={"delivery-date"}>{column.deliveryDate}</div> : null}
                                </th>
                            ))}
                            <th className={"total-col"}>
                                <OwLabel title={"total"} rowHasSort={true} />
                            </th>
                        </tr>
                    </thead>
                    {bodySections}
                    {/* tbody rather than tfoot so browsers do not repeat the totals on every printed page */}
                    <tbody>
                        {renderTotalsRow(
                            <OwLabel title={"total_pieces"} noBottomMargin={true} />,
                            matrix.columnTotals,
                            matrix.grandTotal,
                            "total-pieces"
                        )}
                    </tbody>
                </table>
            </div>
        );
    };

    const renderPickListBody = (data: IOnGetOrdersPickListDTO | undefined, sortable: boolean) => {
        switch (pickListLayout) {
            case "MATRIX":
                return renderMatrixTable(data, sortable);

            default:
                return renderPickListTable(data, sortable);
        }
    };

    const defaultRenderer = () => {
        // Grouped daily routes mode
        if (hasDailyRoutes && groupedPickListData.size > 0) {
            return (
                <div className={"SimplePickList " + template + layoutClass}>
                    {matrixPageStyle}
                    {landscapePageStyle}
                    {footerPageStyle}
                    <div className={"mt-2 mb-2 d-flex justify-content-between align-items-center flex-wrap"}>
                        <div className={"d-inline-flex"}>
                            <Breadcrumbs hasTopMargin={false} navItems={navItems} />
                        </div>
                        <div className={"d-print-none"}>
                            <OwPrintButton />
                            <div className={"ms-auto w-10r"}>
                                <OwSelectOption
                                    valueProp={"value"}
                                    titleProp={"label"}
                                    fn={selectTemplate}
                                    name={"template"}
                                    options={packingListTemplates}
                                    emitObject={true}
                                    selectedOption={template}
                                    hasSelectedOptionProp={false}
                                />
                            </div>
                        </div>
                    </div>
                    <div className={"d-print-none"}>
                        <OwButton title={"view_packing_lists"} fn={viewPackingLists} className={"btn-primary ms-2"} />
                        <OwButton
                            leftIconClass={"fad fa-download"}
                            title={"export_to_csv"}
                            fn={exportPickListCSV}
                            className={"btn-primary ms-2"}
                        />
                        <div className={"mt-2"}>
                            <OwCheckbox
                                fn={() => {
                                    setPrintOrders(!printOrders);
                                }}
                                checked={printOrders}
                                label={"print_orders_detail"}
                            />
                        </div>
                    </div>
                    {dailyRouteGroups.map((group) => {
                        const groupData = groupedPickListData.get(group.dailyRoute.guid);
                        if (!groupData) return null;

                        return (
                            <div key={group.dailyRoute.guid} className={"PickListGroup"}>
                                <DailyRouteCoverSheet
                                    dailyRouteName={group.dailyRoute.name || ""}
                                    routeName={group.dailyRoute.Route?.name || ""}
                                    routeDate={moment(group.dailyRoute.route_date).format("MM/DD/YYYY")}
                                    driverName={group.dailyRoute.Driver?.name || "-"}
                                    vehicleName={group.dailyRoute.Vehicle?.name || "-"}
                                    orderCount={group.orderGuids.length}
                                />
                                <div>
                                    <OwTitle title={"pick_list"} />
                                    {renderOrdersDetail(groupData)}
                                    {renderPickListBody(groupData, false)}
                                </div>
                            </div>
                        );
                    })}
                    <OwViewOptionsPanel
                        saveFn={saveViewOptions}
                        showSaveBtn={true}
                        viewOptions={optionsPanelViewOptions}
                        onChangeFn={handleViewOptionChange}
                    />
                </div>
            );
        }

        // Standard single pick list mode
        return (
            <div className={"SimplePickList " + template + layoutClass}>
                {matrixPageStyle}
                {landscapePageStyle}
                {footerPageStyle}
                <div className={"mt-2 mb-2 d-flex justify-content-between align-items-center flex-wrap"}>
                    <div className={"d-inline-flex"}>
                        <Breadcrumbs hasTopMargin={false} navItems={navItems} />
                    </div>
                    <div className={"d-print-none"}>
                        <OwPrintButton />
                        <div className={"ms-auto w-10r"}>
                            <OwSelectOption
                                valueProp={"value"}
                                titleProp={"label"}
                                fn={selectTemplate}
                                name={"template"}
                                options={packingListTemplates}
                                emitObject={true}
                                selectedOption={template}
                                hasSelectedOptionProp={false}
                            />
                        </div>
                    </div>
                </div>
                <div>
                    <OwTitle title={"pick_list"} />
                    <OwButton
                        leftIconClass={"fad fa-download"}
                        title={"export_to_csv"}
                        fn={exportPickListCSV}
                        className={"btn-primary d-print-none"}
                    />
                    <OwButton title={"view_packing_lists"} fn={viewPackingLists} className={"btn-primary ms-2 d-print-none"} />
                    {orders}
                    {renderPickListBody(pickListData, true)}
                </div>
                <OwViewOptionsPanel
                    saveFn={saveViewOptions}
                    showSaveBtn={true}
                    viewOptions={optionsPanelViewOptions}
                    onChangeFn={handleViewOptionChange}
                />
            </div>
        );
    };

    const preFormComponent = () => {
        return (
            <div>
                <PreformPickList notifyFn={uiContext.notifyFn} socket={socket} data={pickListData} toggleMenu={uiContext.toggleMenu} />
            </div>
        );
    };

    {
        switch (supplierModel.pick_list_component) {
            case PickListComponentEnum.PREFORM:
                return preFormComponent();

            default:
                return defaultRenderer();
        }
    }
};
